import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Target, 
  TrendingUp, 
  TrendingDown,
  IndianRupee,
  BarChart3,
  RefreshCw,
  Download,
  Filter,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Store,
  ArrowUpRight,
  ArrowDownRight,
  Award,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Cell,
  PieChart,
  Pie,
  AreaChart,
  Area
} from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TargetVsAchievement = () => {
  const [data, setData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const { languageConfig } = useLanguage();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (selectedStore) params.store_code = selectedStore;

      const [mainRes, trendRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/target-vs-achievement`, {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${BACKEND_URL}/api/analytics/target-vs-achievement/trend`, {
          params: { days: 30 },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setData(mainRes.data);
      setTrendData(trendRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load Target vs Achievement analytics');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedStore]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = () => {
    if (!data?.detailed_data) return;
    
    const headers = ['Store Code', 'Target Value', 'Actual Value', 'Achievement %', 'Gap', 'Target Qty', 'Actual Qty', 'Status'];
    const csvContent = [
      headers.join(','),
      ...data.detailed_data.map(row => [
        row.store_code,
        row.target_value,
        row.actual_value,
        row.value_achievement,
        row.value_gap,
        row.target_qty,
        row.actual_qty,
        row.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `target_vs_achievement_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Data exported successfully');
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value.toFixed(0)}`;
  };

  const formatNumber = (value) => {
    if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'achieved': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'on_track': return <TrendingUp className="h-4 w-4 text-blue-500" />;
      case 'at_risk': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'critical': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      achieved: 'bg-green-100 text-green-700 border-green-200',
      on_track: 'bg-blue-100 text-blue-700 border-blue-200',
      at_risk: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      critical: 'bg-red-100 text-red-700 border-red-200'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="target-achievement-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const statusCounts = summary.status_counts || {};
  
  // Prepare pie chart data
  const pieData = [
    { name: 'Achieved', value: statusCounts.achieved || 0, fill: '#22c55e' },
    { name: 'On Track', value: statusCounts.on_track || 0, fill: '#3b82f6' },
    { name: 'At Risk', value: statusCounts.at_risk || 0, fill: '#eab308' },
    { name: 'Critical', value: statusCounts.critical || 0, fill: '#ef4444' }
  ].filter(d => d.value > 0);

  // Prepare comparison chart data
  const comparisonData = data?.detailed_data?.slice(0, 15).map(d => ({
    store: `Store ${d.store_code}`,
    target: d.target_value,
    actual: d.actual_value,
    achievement: d.value_achievement
  })) || [];

  return (
    <div className="space-y-6" data-testid="target-vs-achievement-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            <TranslatedText text="Target vs. Achievement" cacheKey="targetachieve:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Track sales performance against targets across stores" cacheKey="targetachieve:subtitle" />
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            data-testid="refresh-target-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            data-testid="export-target-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-50 dark:bg-slate-800/50">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filters:</span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-40"
                placeholder="From Date"
                data-testid="target-date-from"
              />
              <span className="text-slate-500">to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-40"
                placeholder="To Date"
                data-testid="target-date-to"
              />
            </div>
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white"
              data-testid="target-store-filter"
            >
              <option value="">All Stores</option>
              {data?.stores?.map(store => (
                <option key={store} value={store}>Store {store}</option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={loadData}
              data-testid="target-apply-filters"
            >
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 dark:text-blue-300">Target Value</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100" data-testid="target-value">
                  {formatCurrency(summary.total_target_value || 0)}
                </p>
              </div>
              <Target className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 dark:text-green-300">Actual Value</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100" data-testid="actual-value">
                  {formatCurrency(summary.total_actual_value || 0)}
                </p>
              </div>
              <IndianRupee className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className={`bg-gradient-to-br ${
          summary.overall_value_achievement >= 100 ? 'from-green-50 to-green-100 border-green-200' :
          summary.overall_value_achievement >= 80 ? 'from-blue-50 to-blue-100 border-blue-200' :
          summary.overall_value_achievement >= 50 ? 'from-yellow-50 to-yellow-100 border-yellow-200' :
          'from-red-50 to-red-100 border-red-200'
        }`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-700 dark:text-slate-300">Achievement</p>
                <p className="text-2xl font-bold" data-testid="achievement-pct">
                  {summary.overall_value_achievement || 0}%
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Gap: {formatCurrency(Math.abs(summary.value_gap || 0))}
                  {summary.value_gap > 0 ? ' below' : ' above'}
                </p>
              </div>
              {summary.overall_value_achievement >= 80 ? (
                <ArrowUpRight className="h-8 w-8 text-green-600" />
              ) : (
                <ArrowDownRight className="h-8 w-8 text-red-600" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700 dark:text-purple-300">Stores Analyzed</p>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100" data-testid="stores-count">
                  {summary.stores_analyzed || 0}
                </p>
              </div>
              <Store className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-green-200 bg-green-50/50 dark:bg-green-900/10">
          <CardContent className="pt-4 text-center">
            <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-700">{statusCounts.achieved || 0}</p>
            <p className="text-sm text-green-600">Achieved (100%+)</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-900/10">
          <CardContent className="pt-4 text-center">
            <TrendingUp className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-700">{statusCounts.on_track || 0}</p>
            <p className="text-sm text-blue-600">On Track (80-99%)</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50/50 dark:bg-yellow-900/10">
          <CardContent className="pt-4 text-center">
            <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-700">{statusCounts.at_risk || 0}</p>
            <p className="text-sm text-yellow-600">At Risk (50-79%)</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50 dark:bg-red-900/10">
          <CardContent className="pt-4 text-center">
            <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-700">{statusCounts.critical || 0}</p>
            <p className="text-sm text-red-600">Critical (&lt;50%)</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Target vs Actual Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Target vs Actual by Store
            </CardTitle>
            <CardDescription>Top 15 stores comparison</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="store" 
                  tick={{ fontSize: 10 }}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => formatCurrency(v)}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'Achievement') return [`${value}%`, name];
                    return [formatCurrency(value), name];
                  }}
                />
                <Legend />
                <Bar dataKey="target" name="Target" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" name="Actual" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-green-600" />
              Achievement Distribution
            </CardTitle>
            <CardDescription>Stores by achievement status</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [value, 'Stores']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      {trendData?.trend_data?.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-600" />
              Target vs Actual Trend
            </CardTitle>
            <CardDescription>Daily target and actual sales values over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={trendData.trend_data}>
                <defs>
                  <linearGradient id="colorTarget" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip formatter={(value, name) => [formatCurrency(value), name]} />
                <Legend />
                <Area type="monotone" dataKey="target_value" name="Target" stroke="#94a3b8" fill="url(#colorTarget)" strokeWidth={2} strokeDasharray="5 5" />
                <Area type="monotone" dataKey="actual_value" name="Actual" stroke="#3b82f6" fill="url(#colorActual)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Achievement Gap Waterfall */}
      {data?.detailed_data?.length > 0 && (() => {
        const gapData = data.detailed_data
          .filter(d => d.target_value > 0)
          .sort((a, b) => b.value_gap - a.value_gap)
          .slice(0, 12)
          .map(d => ({
            store: `Store ${d.store_code}`,
            gap: d.value_gap,
            achievement: d.value_achievement,
            status: d.status
          }));
        if (gapData.length < 2) return null;
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-red-600" />
                Gap Analysis by Store
              </CardTitle>
              <CardDescription>Revenue gap (Target - Actual): positive = shortfall, negative = exceeded</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={gapData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="store" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatCurrency(Math.abs(v))} />
                  <Tooltip formatter={(v) => [formatCurrency(Math.abs(v)), v >= 0 ? 'Shortfall' : 'Exceeded']} />
                  <Legend />
                  <Bar dataKey="gap" name="Gap" radius={[4, 4, 0, 0]}>
                    {gapData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.gap > 0 ? '#ef4444' : '#22c55e'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );
      })()}

      {/* Top & Bottom Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <Card className="border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Award className="h-5 w-5" />
              Top Performers
            </CardTitle>
            <CardDescription>Stores exceeding targets</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.top_performers?.slice(0, 5).map((store, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-bold">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium">Store {store.store_code}</p>
                      <p className="text-sm text-slate-500">
                        {formatCurrency(store.actual_value)} / {formatCurrency(store.target_value)}
                      </p>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-green-600">
                    {store.value_achievement}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bottom Performers */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              Needs Attention
            </CardTitle>
            <CardDescription>Stores below target</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.bottom_performers?.slice(0, 5).map((store, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold ${
                      store.status === 'critical' ? 'bg-red-500' : 'bg-yellow-500'
                    }`}>
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium">Store {store.store_code}</p>
                      <p className="text-sm text-slate-500">
                        Gap: {formatCurrency(store.value_gap)}
                      </p>
                    </div>
                  </div>
                  <span className={`text-lg font-bold ${
                    store.status === 'critical' ? 'text-red-600' : 'text-yellow-600'
                  }`}>
                    {store.value_achievement}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Store Performance</CardTitle>
          <CardDescription>
            Complete breakdown of {data?.detailed_data?.length || 0} stores
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="target-achievement-table">
              <thead>
                <tr className="border-b bg-slate-50 dark:bg-slate-800">
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Store</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Target</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Actual</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Achievement</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Gap</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Transactions</th>
                  <th className="px-4 py-3 text-center font-semibold text-slate-700 dark:text-slate-300">Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.detailed_data?.map((row, idx) => (
                  <tr 
                    key={idx} 
                    className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium">Store {row.store_code}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(row.target_value)}</td>
                    <td className="px-4 py-3 text-right text-green-600 font-medium">
                      {formatCurrency(row.actual_value)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${
                        row.value_achievement >= 100 ? 'text-green-600' :
                        row.value_achievement >= 80 ? 'text-blue-600' :
                        row.value_achievement >= 50 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {row.value_achievement}%
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-right ${row.value_gap > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {row.value_gap > 0 ? '-' : '+'}{formatCurrency(Math.abs(row.value_gap))}
                    </td>
                    <td className="px-4 py-3 text-right">{row.transactions.toLocaleString()}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(row.status)}`}>
                        {getStatusIcon(row.status)}
                        {row.status.replace('_', ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TargetVsAchievement;
